import React, { useEffect } from "react";
import styled from "styled-components";
import { C, SEMI, REG, Section, SegLabel, Toggle } from "../Styles";
import { SvcListOne } from "./StepVerify";

// ─── Build EXCLUSIVE_PAIRS map dynamically from config ───────────────────────
// Supports the new `exclusiveWith: [array]` schema from ServicesConfigEditor.
export const buildExclusivePairs = (services = []) => {
  const pairs = {};
  services.forEach(({ key, exclusiveWith }) => {
    if (exclusiveWith?.length) {
      pairs[key] = exclusiveWith;
    }
  });
  return pairs;
};

// Legacy static fallback (used if dynamic config not passed)
export const EXCLUSIVE_PAIRS = {
  transcriptionServices: ["stenographerServices"],
  stenographerServices: ["transcriptionServices"],
};

// ─── Price display helpers ────────────────────────────────────────────────────

const getDurationHours = (duration) => {
  if (duration === "fullday") return 6;
  if (duration === "halfday") return 3;
  return 1;
};

const getEffectivePrice = (pricing, duration) => {
  if (!pricing) return null;

  const multiplier = duration === "fullday" ? 2 : 1;
  const durationHours = getDurationHours(duration);

  if (pricing.offerPrice) {
    if (pricing.perHour) {
      return Number(pricing.offerPrice) * durationHours;
    }
    return Number(pricing.offerPrice) * multiplier;
  }

  if (pricing.flat) {
    return Number(pricing.flat) * multiplier;
  }

  if (pricing.perHour) {
    return Number(pricing.perHour) * durationHours;
  }

  return null;
};

export const calculateBookingCharges = (
  supportServices = {},
  cateringServices = {},
  roomCharge = 0,
  duration = "halfday",
) => {
  let serviceTotal = 0;

  Object.values(supportServices || {}).forEach((svc) => {
    if (!svc || svc.type === "ON_REQUEST") return;
    const effectivePrice = getEffectivePrice(svc.pricing, duration);
    if (effectivePrice != null) serviceTotal += effectivePrice;
  });

  Object.values(cateringServices || {}).forEach((svc) => {
    if (!svc || svc.type === "ON_REQUEST") return;
    const effectivePrice = getEffectivePrice(svc.pricing, duration);
    if (effectivePrice != null) serviceTotal += effectivePrice;
  });

  const finalRoomCharge = Number(roomCharge || 0);
  const subTotal = finalRoomCharge + serviceTotal;
  const gstAmount = subTotal * 0.18;
  const totalAmount = subTotal + gstAmount;

  return {
    room_charge: finalRoomCharge,
    service_charge: serviceTotal,
    sub_total: subTotal,
    gst_amount: gstAmount,
    total_amount: totalAmount,
  };
};

const formatPrice = (pricing, duration) => {
  if (!pricing) return null;

  const multiplier = duration === "fullday" ? 2 : 1;

  const baseLabel = pricing.flat
    ? `₹ ${(Number(pricing.flat) * multiplier).toLocaleString()}`
    : pricing.perHour
      ? `₹ ${(Number(pricing.perHour) * multiplier).toLocaleString()} / hr`
      : null;

  const offerLabel = pricing.offerPrice
    ? pricing.perHour
      ? `₹ ${(Number(pricing.offerPrice) * multiplier).toLocaleString()} / hr`
      : `₹ ${(Number(pricing.offerPrice) * multiplier).toLocaleString()}`
    : null;

  if (offerLabel && baseLabel) {
    return (
      <span>
        <span
          style={{
            textDecoration: "line-through",
            marginRight: 6,
            color: C.muted,
          }}
        >
          {baseLabel}
        </span>
        <span>{offerLabel}</span>
      </span>
    );
  }

  return baseLabel;
};

const isOnRequest = (type) => type === "ON_REQUEST" || type === "ON REQUEST";

// ─── Component ────────────────────────────────────────────────────────────────

/*

const calculateServiceCharge = (
  supportServices = {},
  cateringServices = {},
  servicesPricingInfo = [],
  cateringServicesInfo = [],
) => {
  let total = 0;

  // Support services (object)
  Object.values(supportServices || {}).forEach((svc) => {
    if (!svc || svc.type === "ON_REQUEST") return;

    if (svc.pricing?.flat) {
      total += Number(svc.pricing.flat);
    } else if (svc.pricing?.perHour) {
      total += Number(svc.pricing.perHour); // you can multiply by hours if needed
    }
  });

  // Catering services (boolean toggle)
  Object.entries(cateringServices || {}).forEach(([key, active]) => {
    if (!active) return;

    const svc = cateringServicesInfo.find((s) => s.key === key);
    if (!svc || svc.type === "ON_REQUEST") return;

    if (svc.pricing?.flat) {
      total += Number(svc.pricing.flat);
    } else if (svc.pricing?.perHour) {
      total += Number(svc.pricing.perHour);
    }
  });

  return total;
};

*/

const StepServices = ({
  values,
  setFieldValue,
  servicesPricingInfo,
  cateringServicesInfo,
}) => {
  useEffect(
    () => {
      const charges = calculateBookingCharges(
        values.supportServices,
        values.cateringServices,
        values.room_charge,
        values.session_duration,
      );

      setFieldValue("service_charge", charges.service_charge);
      setFieldValue("sub_total", charges.sub_total);
      setFieldValue("gst_amount", charges.gst_amount);
      setFieldValue("total_amount", charges.total_amount);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      values.supportServices,
      values.cateringServices,
      values.room_charge,
      values.session_duration,
    ],
  );

  // Build exclusive-pairs map from live config
  const exclusivePairsMap = React.useMemo(
    () => buildExclusivePairs(servicesPricingInfo || []),
    [servicesPricingInfo],
  );

  // Find any active exclusive peer keys for a given service key
  const getActivePeers = (key) => {
    const peers = exclusivePairsMap[key] || [];
    return peers.filter((peerKey) => !!values.supportServices?.[peerKey]);
  };

  const handleSupportToggle = (service) => {
    const { key, serviceName, pricing, type, hsnCode } = service;
    const isActive = !!values.supportServices?.[key];

    if (isActive) {
      setFieldValue(`supportServices.${key}`, null);
    } else {
      // Deselect all exclusive peers
      const peers = exclusivePairsMap[key] || [];
      peers.forEach((peerKey) => {
        if (values.supportServices?.[peerKey]) {
          setFieldValue(`supportServices.${peerKey}`, null);
        }
      });
      setFieldValue(`supportServices.${key}`, {
        key,
        serviceName,
        pricing,
        type,
        hsnCode,
      });
    }
  };

  // Transcription Services is selected by default while its price is ₹0.
  // Applied once per booking, so a deselect by the user is kept when the
  // step is revisited.
  useEffect(
    () => {
      if (values.transcriptionDefaultApplied) return;
      const service = servicesPricingInfo?.find(
        (s) => s.key === "transcriptionServices" && s.active !== false,
      );
      if (!service) return;

      setFieldValue("transcriptionDefaultApplied", true);
      if (
        isOnRequest(service.type) ||
        getEffectivePrice(service.pricing, values.session_duration) !== 0 ||
        values.supportServices?.[service.key] ||
        getActivePeers(service.key).length
      )
        return;
      handleSupportToggle(service);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [servicesPricingInfo],
  );

  // Collect unique exclusive pairs to show in the notice
  const exclusiveNotices = React.useMemo(() => {
    const seen = new Set();
    const notices = [];
    Object.entries(exclusivePairsMap).forEach(([key, peers]) => {
      const pairId = [key, ...peers].sort().join("|");
      if (!seen.has(pairId)) {
        seen.add(pairId);
        const keyService = servicesPricingInfo?.find((s) => s.key === key);
        const peerServices = peers.map((p) =>
          servicesPricingInfo?.find((s) => s.key === p),
        );
        if (keyService && peerServices.every(Boolean)) {
          notices.push({
            names: [
              keyService.serviceName,
              ...peerServices.map((s) => s.serviceName),
            ],
          });
        }
      }
    });
    return notices;
  }, [exclusivePairsMap, servicesPricingInfo]);

  const toggleCateringService = (service) => {
    const existing = values.cateringServices?.[service.key];

    if (existing) {
      const updated = { ...values.cateringServices };
      delete updated[service.key];
      setFieldValue("cateringServices", updated);
    } else {
      setFieldValue(`cateringServices.${service.key}`, {
        ...service,
        active: true,
      });
    }
  };

  return (
    <>
      {/* ── Catering Services ── */}
      <Section $delay="0s">
        <SegLabel>Catering Services</SegLabel>
        <SvcListOne>
          {cateringServicesInfo
            ?.filter((s) => s.active !== false)
            .map((service) => {
              const { key, serviceName, pricing, type } = service;
              const active = !!values.cateringServices?.[key];

              const priceLabel = formatPrice(pricing, values.session_duration);
              const onRequest = isOnRequest(type);

              return (
                <SvcRow
                  key={key}
                  type="button"
                  $active={active}
                  onClick={() => toggleCateringService(service)}
                >
                  <SvcRowInner>
                    <SvcNameRow>
                      <SvcName $active={active}>{serviceName}</SvcName>
                      {priceLabel && !onRequest && (
                        <SvcPriceBadge $active={active}>
                          {priceLabel}
                        </SvcPriceBadge>
                      )}

                      {onRequest && <SvcTypeBadge>ON REQUEST</SvcTypeBadge>}
                    </SvcNameRow>
                  </SvcRowInner>

                  <Toggle $on={active} />
                </SvcRow>
              );
            })}
        </SvcListOne>
      </Section>

      {/* ── Support Services ── */}
      <Section $delay="0.05s">
        <SegLabel>Support Services</SegLabel>

        {/* Dynamic mutual exclusion notices */}
        {exclusiveNotices.map((notice, i) => (
          <ExclusiveNote key={i}>
            <ExclusiveNoteIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 8v4M12 16h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ExclusiveNoteIcon>
            {notice.names.map((name, idx) => (
              <React.Fragment key={name}>
                <strong>{name}</strong>
                {idx < notice.names.length - 2 && ", "}
                {idx === notice.names.length - 2 && " and "}
              </React.Fragment>
            ))}{" "}
            are mutually exclusive — only one may be selected at a time.
          </ExclusiveNote>
        ))}

        <SvcList>
          {servicesPricingInfo
            ?.filter((s) => s.active !== false)
            .map((service) => {
              const { key, serviceName, pricing, type } = service;
              const active = !!values.supportServices?.[key];
              const activePeers = getActivePeers(key);
              const isDisabled = activePeers.length > 0;
              const priceLabel = formatPrice(pricing, values.session_duration);
              const onRequest = isOnRequest(type);

              // Build disabled hint from peer service names
              const disabledHint = isDisabled
                ? activePeers
                    .map(
                      (peerKey) =>
                        servicesPricingInfo?.find((s) => s.key === peerKey)
                          ?.serviceName || peerKey,
                    )
                    .map((name) => `Cannot select with ${name}`)
                    .join(" · ")
                : null;

              return (
                <SvcRow
                  key={key}
                  type="button"
                  $active={active}
                  $disabled={isDisabled}
                  onClick={() => !isDisabled && handleSupportToggle(service)}
                >
                  <SvcRowInner>
                    <SvcNameRow>
                      <SvcName $active={active} $disabled={isDisabled}>
                        {serviceName}
                      </SvcName>
                      {priceLabel && !onRequest && (
                        <SvcPriceBadge $active={active}>
                          {priceLabel}
                        </SvcPriceBadge>
                      )}
                      {onRequest && <SvcTypeBadge>ON REQUEST</SvcTypeBadge>}
                    </SvcNameRow>
                    <SvcMetaRow>
                      {isDisabled && disabledHint && (
                        <DisabledHint>{disabledHint}</DisabledHint>
                      )}
                    </SvcMetaRow>
                  </SvcRowInner>
                  <Toggle
                    $on={active}
                    style={{ opacity: isDisabled ? 0.4 : 1 }}
                  />
                </SvcRow>
              );
            })}
        </SvcList>
      </Section>
    </>
  );
};

export default StepServices;

export const SvcList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  border: 1.5px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 8px;
  background: ${C.white};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const SvcRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  border: none;
  border-bottom: 1px solid ${C.line};
  background: ${(p) =>
    p.$disabled ? C.surface : p.$active ? "rgba(0,131,140,0.07)" : C.white};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  text-align: left;
  transition: all 0.2s ease;
  position: relative;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  &:last-child {
    border-bottom: none;
  }

  &:hover:not([disabled]) {
    background: ${(p) =>
      p.$disabled ? C.surface : p.$active ? "rgba(0,131,140,0.09)" : C.surface};
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${(p) => (p.$active && !p.$disabled ? C.teal : "transparent")};
    transition: background 0.2s;
  }
`;

const SvcRowInner = styled.div`
  flex: 1;
  min-width: 0;
`;

const SvcNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const SvcMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const SvcName = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13.5px;
  color: ${(p) => (p.$disabled ? C.muted : p.$active ? C.tealDark : C.navy)};
  letter-spacing: -0.01em;
  transition: color 0.2s;
`;

export const SvcSub = styled.div`
  font-family: ${REG};
  font-size: 11.5px;
  color: ${(p) => (p.$disabled ? C.mutedLight : p.$active ? C.teal : C.muted)};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  transition: color 0.2s;
  line-height: 1.45;
`;

const SvcPriceBadge = styled.span`
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 99px;
  background: ${(p) => (p.$active ? "rgba(0,131,140,0.15)" : C.tealAlpha)};
  border: 1px solid ${C.tealLine};
  color: ${C.teal};
  white-space: nowrap;
`;

const SvcTypeBadge = styled.span`
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 99px;
  background: rgba(246, 180, 54, 0.12);
  border: 1px solid rgba(246, 180, 54, 0.28);
  color: #c98a00;
  white-space: nowrap;
  letter-spacing: 0.04em;
`;

const DisabledHint = styled.span`
  font-family: ${SEMI};
  font-size: 10px;
  color: ${C.error};
  letter-spacing: 0.02em;
`;

const ExclusiveNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(246, 180, 54, 0.08);
  border: 1px solid rgba(246, 180, 54, 0.24);
  font-family: ${REG};
  font-size: 12.5px;
  color: #7a5200;
  line-height: 1.6;
  margin-bottom: 14px;

  strong {
    font-family: ${SEMI};
    font-weight: 700;
  }
`;

const ExclusiveNoteIcon = styled.div`
  color: #c98a00;
  flex-shrink: 0;
  margin-top: 1px;
`;
